import React, { useState, useRef } from "react";
import { importAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Upload,
  FileSpreadsheet,
  Download,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  FileText,
  ArrowRight,
} from "lucide-react";

export function ImportPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState(null);
  const fileInputRef = useRef(null);

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      showMessage("Formato file non supportato. Usa CSV o Excel.", "error");
      return;
    }

    setFile(selectedFile);
    setPreview(null);
    setResult(null);
    
    // Auto-preview
    setLoading(true);
    try {
      const response = await importAPI.preview(selectedFile);
      setPreview(response.data);
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore nella lettura del file", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    try {
      const response = await importAPI.execute(file);
      setResult(response.data);
      setPreview(null);
      showMessage(`Importati ${response.data.total_imported} protocolli con successo!`);
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante l'import", "error");
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await importAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_import_protocolli.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      showMessage("Errore nel download del template", "error");
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" 
            ? "bg-red-500 text-white" 
            : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <Upload size={32} className="text-[#2D7BB7]" />
            Import Protocolli
          </h1>
          <p className="text-muted-foreground mt-1">
            Importa protocolli da file CSV o Excel
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleDownloadTemplate}
          className="border-border text-foreground"
          data-testid="download-template"
        >
          <Download className="mr-2" size={18} />
          Scarica Template
        </Button>
      </div>

      {/* Upload Area */}
      {!preview && !result && (
        <Card className="bg-card border-border">
          <CardContent className="p-8">
            <div
              className="border-2 border-dashed border-border rounded-xl p-12 text-center hover:border-[#2D7BB7] transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {loading ? (
                <Loader2 size={48} className="mx-auto mb-4 text-[#2D7BB7] animate-spin" />
              ) : (
                <FileSpreadsheet size={48} className="mx-auto mb-4 text-muted-foreground" />
              )}
              <p className="text-lg text-foreground mb-2">
                {loading ? "Analisi file in corso..." : "Clicca o trascina un file qui"}
              </p>
              <p className="text-sm text-muted-foreground">
                Formati supportati: CSV, Excel (.xlsx, .xls)
              </p>
            </div>

            <div className="mt-6 p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium text-foreground mb-2">Formato richiesto:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
                <span>• Direzione (obbligatorio)</span>
                <span>• Oggetto (obbligatorio)</span>
                <span>• Tipologia</span>
                <span>• Mittente</span>
                <span>• Destinatario</span>
                <span>• Note</span>
                <span>• Data</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {preview && (
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                <FileText size={20} className="text-[#2D7BB7]" />
                Anteprima: {file?.name}
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={resetImport}
                className="text-muted-foreground"
              >
                <X size={20} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-muted rounded-lg text-center">
                <p className="text-2xl font-bold text-foreground">{preview.total_rows}</p>
                <p className="text-sm text-muted-foreground">Righe totali</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg text-center border border-emerald-200">
                <p className="text-2xl font-bold text-emerald-600">{preview.valid_rows}</p>
                <p className="text-sm text-emerald-700">Righe valide</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center border border-red-200">
                <p className="text-2xl font-bold text-red-600">{preview.invalid_rows}</p>
                <p className="text-sm text-red-700">Righe con errori</p>
              </div>
            </div>

            {/* Table Preview */}
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto border rounded-lg">
              <Table>
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow className="border-border">
                    <TableHead className="text-muted-foreground w-12">#</TableHead>
                    <TableHead className="text-muted-foreground">Stato</TableHead>
                    <TableHead className="text-muted-foreground">Direzione</TableHead>
                    <TableHead className="text-muted-foreground">Tipologia</TableHead>
                    <TableHead className="text-muted-foreground">Oggetto</TableHead>
                    <TableHead className="text-muted-foreground">Mittente</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((row) => (
                    <TableRow
                      key={row.row_number}
                      className={`border-border ${!row.is_valid ? "bg-red-50" : ""}`}
                    >
                      <TableCell className="text-muted-foreground">{row.row_number}</TableCell>
                      <TableCell>
                        {row.is_valid ? (
                          <CheckCircle size={18} className="text-emerald-500" />
                        ) : (
                          <div className="flex items-center gap-2">
                            <AlertCircle size={18} className="text-red-500" />
                            <span className="text-xs text-red-600">{row.errors[0]}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={row.direction === "entrata" 
                          ? "bg-blue-100 text-blue-800 border-blue-200" 
                          : "bg-amber-100 text-amber-800 border-amber-200"
                        }>
                          {row.direction || "-"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-foreground">{row.document_type}</TableCell>
                      <TableCell className="text-foreground max-w-[200px] truncate">
                        {row.subject}
                      </TableCell>
                      <TableCell className="text-foreground">{row.sender || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={resetImport}
                className="border-border text-foreground"
              >
                Annulla
              </Button>
              <Button
                onClick={handleImport}
                disabled={importing || preview.valid_rows === 0}
                className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
                data-testid="execute-import"
              >
                {importing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="mr-2" size={18} />
                )}
                Importa {preview.valid_rows} protocolli
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <CheckCircle size={20} className="text-emerald-500" />
              Import Completato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-emerald-50 rounded-lg text-center border border-emerald-200">
                <p className="text-3xl font-bold text-emerald-600">{result.total_imported}</p>
                <p className="text-sm text-emerald-700">Protocolli importati</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center border border-red-200">
                <p className="text-3xl font-bold text-red-600">{result.total_errors}</p>
                <p className="text-sm text-red-700">Errori</p>
              </div>
            </div>

            {result.protocols_created.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-foreground mb-2">Protocolli creati:</p>
                <div className="flex flex-wrap gap-2">
                  {result.protocols_created.slice(0, 20).map((pn) => (
                    <Badge key={pn} className="bg-[#2D7BB7]/10 text-[#2D7BB7] border-[#2D7BB7]/20">
                      {pn}
                    </Badge>
                  ))}
                  {result.protocols_created.length > 20 && (
                    <Badge className="bg-muted text-muted-foreground">
                      +{result.protocols_created.length - 20} altri
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-medium text-red-800 mb-2">Errori:</p>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {result.errors.map((err, idx) => (
                    <p key={idx} className="text-xs text-red-700">
                      Riga {err.row}: {err.error}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-border">
              <Button
                onClick={resetImport}
                className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
              >
                Importa altri protocolli
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
